/**
 * Shared local-storage bookkeeping for "this browser already has a check-in on this apartment".
 * Written whenever a guest reaches the Guest Form (either via Step 1's own submit, or by landing
 * on the form directly from an emailed/shared link) so a later visit to the permanent apartment
 * link can recognize them without asking again.
 *
 * Valid only through the reservation's checkout date — once the stay is over there's no reason
 * to keep auto-recognizing the browser (the Guide itself expires post-checkout too, and the same
 * link may later be used by a different guest on a shared/kiosk device).
 */
export interface StoredCheckin {
  formToken: string;
  checkInDate: string;
  checkOutDate: string;
  numberOfGuests: number;
  savedAt: number;
}

export const CHECKIN_STORAGE_KEY = (publicKey: string) => `guestbook-checkin-${publicKey}`;
/** Hard safety-net cap in case checkOutDate is ever missing/malformed. */
export const CHECKIN_TTL_MS = 90 * 24 * 60 * 60 * 1000;

export const saveCheckinEntry = (publicKey: string, entry: Omit<StoredCheckin, 'savedAt'>): void => {
  const stored: StoredCheckin = { ...entry, savedAt: Date.now() };
  localStorage.setItem(CHECKIN_STORAGE_KEY(publicKey), JSON.stringify(stored));
};

const todayIsoDate = (): string => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/** True once the reservation's checkout date has fully passed, or the 90-day safety cap is hit. */
export const isCheckinExpired = (entry: StoredCheckin): boolean => {
  if (entry.checkOutDate && entry.checkOutDate < todayIsoDate()) return true;
  return Date.now() - entry.savedAt >= CHECKIN_TTL_MS;
};

export const readCheckinEntry = (publicKey: string): StoredCheckin | null => {
  const raw = localStorage.getItem(CHECKIN_STORAGE_KEY(publicKey));
  if (!raw) return null;
  try {
    const parsed: StoredCheckin = JSON.parse(raw);
    if (isCheckinExpired(parsed)) {
      localStorage.removeItem(CHECKIN_STORAGE_KEY(publicKey));
      return null;
    }
    return parsed;
  } catch {
    localStorage.removeItem(CHECKIN_STORAGE_KEY(publicKey));
    return null;
  }
};
